import React, {cloneElement, useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer
} from "../../common/AbstractListView";

import constants from '../../../constants';
import MoveTypeDropDown from "../../../uiControls/MoveTypeDropDown";
import {Search} from "../../../filtering";

import {NavigationType, openNewPage} from '../../../util/navigationHelpers';

import {
  DateRenderer,
  DateTimeRenderer,
  FloatRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextAlignRightRenderer,
  TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import {JobNames} from "../../../framework.core/JobNames";
import {oceanExportContainerStatusList} from "../tableSettings";
import {OeContainerListItem} from "../../../fms.data/models/OeContainerListItem";
import {OceanQuoteListItem} from "../../../fms.data/models/OceanQuoteListItem";
import IfmsModal from "../../../ui/IfmsModal";
import FlexTank from "../../reports/FlexTank";
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import * as XCol from '../../../framework.core/XExportColumn';
import { FmsSecTask } from '../../../fms.core/constants';
import { findSecurityTask } from '../../../util';
import FlexiTank2 from '../../../img/Flexi Tank2.svg';
import FlexiTank2_D from '../../../img/Flexi Tank_Dark2.svg';
import IconButton from '../../../ui/IconButton';
import { SearchModelBase } from '../../../fms.data/models/SearchModelBase';
import { addMonths, formatDate, nowDate } from '../../../dateUtils';
import periodCloseIcon from '../../../img/PeriodClose.svg';
import periodCloseIcon2 from '../../../img/periodclose-12.svg';
import opCloseIcon from '../../../img/OPClose.svg';
import opCloseIcon2 from '../../../img/opclose-12.svg';
import accountCloseIcon from '../../../img/AccountClose.svg';
import accountCloseIcon2 from '../../../img/accountingclose-12.svg';

import { changeRQEvent, LastEventRenderer, RecognizedFilterRenderer, RQFilterEventRenderer, RQFilterRenderer } from '../../common/commonListRenderers';
import { SuntekSettings, SystemSettings } from '../../../ifmsTypes';
import { displayLastTrackingEvent } from '../../common/commonExportFormat';
import cntrBlue from '../../../img/cntr-tracking-rq-12-blue.svg';
import cntrGreen from '../../../img/cntr-tracking-rq-12-green.svg';

function MoveTypeFilterRenderer({value, onChange}: any) {
  return (
    <MoveTypeDropDown
      name='ifms-filter-moveType-select'
      value={value}
      onChange={moveType => onChange(moveType)}      
    />
  );
}

function IndicatorRenderer({ data, row }: any) {
  const l = data[row.index];

  let dataTooltip = '';
  const lStatus = (l as any).status as string;
  if (lStatus === 'PC') dataTooltip = 'Period Close';
  else if (lStatus === 'OC') dataTooltip = 'OP Close';
  else if (lStatus === 'AC') dataTooltip = 'Accounting Close';

  let content: React.ReactNode = <></>;
  if (lStatus === 'PC')
    content = <img height="12px" src={periodCloseIcon2} id={'redIcon'} style={{ position: 'relative', top: '0px', marginTop: '4px' }} alt=""/>;
  else if (lStatus === 'OC')
    content = <img height="12px" src={opCloseIcon2} id={'blueIcon'} style={{ position: 'relative', top: '0px', marginTop: '4px' }} alt=""/>;
  else if (lStatus === 'AC')
    content = <img height="12px" src={accountCloseIcon2} id={'blackIcon'} style={{ position: 'relative', top: '0px', marginTop: '4px' }} alt=""/>;

  return (
    <div className="ifms-table-default" id={`ifms-table-default-${row.index}`}  data-tooltip={dataTooltip} style={{ width: '100%', minHeight: '22px'}}>{content}</div>
  );
}

function useOperations() {
  const history = useHistory();
  const jobListContext = useContext(JobListViewContext);

  function switchToHblFor(c: OeContainerListItem | null) {
    if (!c) {
      return;
    }

     jobListContext.setFilterForScreenExternally('/common/joblistview/oeHbl/oceanExport', 
      'id', 'HBL', c.hblId, 'u2Date', false, true, null, 'hblNo', c.hblNo, false,true);
    
    openNewPage(history, `/common/joblistview/oeHbl/oceanExport`, NavigationType.newPage);
  }

  function switchToMblFor(c: OeContainerListItem | null) {
    if (!c) {
      return;
    }

    jobListContext.setFilterForScreenExternally('/common/joblistview/oeMbl/oceanExport', 
    'id', 'Ref. #', c.oeMblId, 'u2Date', false, true, null, 'refNo', c.refNo, false,true);
    
    openNewPage(history, `/common/joblistview/oeMbl/oceanExport`, NavigationType.newPage);
  }

  function switchToCntrFor(c: OeContainerListItem | null) {
    if (!c || !c.containerNo) {
      return;
    }

    const todayBefore3Month = addMonths(nowDate(), -3);
    let model = new SearchModelBase();
    model.extraWhere = `ContainerNo in ('${c.containerNo}') and Etd >= '${todayBefore3Month}'`;
    jobListContext.setFilterForScreenExternally('/common/joblistview/oeHbl/oceanExport', 
      'containerNo', 'Container', c.containerNo, 'u2Date', false, true, model);
    openNewPage(history, `/common/joblistview/oeHbl/oceanExport`, NavigationType.newPage);
  }

  return {switchToMblFor, switchToHblFor, switchToCntrFor};
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();

  const { switchToMblFor, switchToHblFor } = useOperations();

  const selectedElement = jobListContext.selectedElement as OeContainerListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl('/oceanExport/containerStatus/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, '/oceanExport/containerStatus/add', NavigationType.newPage);
  }

  async function deleteMbl() {
    if (!selectedElement) {
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.Container, { id:selectedElement.containerId, pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message);
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.containerId;
    const editUrl = `/oceanExport/containerStatus/edit/${id}`;
    const viewUrl = `/oceanExport/containerStatus/view/${id}`;

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.containerId;

    const editUrl = `/oceanExport/containerStatus/edit/${id}`;
    const viewUrl = `/oceanExport/containerStatus/view/${id}`;
    const listSec = findSecurityTask(appContext, FmsSecTask.OEContainer);

    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl('/oceanExport/containerStatus/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.containerId;
    openNewPage(history, `/oceanExport/containerStatus/add?copyId=${id}`, NavigationType.newPage);
  }

  function switchToMbl() {
    const selectedElement = jobListContext.selectedElement as OeContainerListItem;
    switchToMblFor(selectedElement);
  }

  function switchToHbl() {
    const selectedElement = jobListContext.selectedElement as OeContainerListItem;
    switchToHblFor(selectedElement);
  }

  return {create, deleteMbl, edit, view, copy, switchToMbl, switchToHbl};
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const mblSec = findSecurityTask(appContext, FmsSecTask.OEMbl);
  const hblSec = findSecurityTask(appContext, FmsSecTask.OEHbl);

  const {switchToMbl, switchToHbl} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {
      if (mblSec.grantRead)
        switchToMbl();
    } else if (unprocessedFkey === 'F7') {
      if (hblSec.grantRead)
        switchToHbl();
    }else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F7','F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }


  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.OEContainer}
      jobId={(jobListContext.selectedElement as OeContainerListItem | null)?.containerId ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const mblSec = findSecurityTask(appContext, FmsSecTask.OEMbl);
  const hblSec = findSecurityTask(appContext, FmsSecTask.OEHbl);
  const {switchToMbl, switchToHbl} = useSelectedOperations();

  const items = [
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: 'Go to MBL List', shortcut: 'F6', separator: false, newCol: false, hidden:!mblSec?.grantRead },
    { label: 'Go to HBL List', shortcut: 'F7', separator: true, hidden:!hblSec?.grantRead },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item: IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      case 1:
         switchToMbl();
        break;
      case 2:
        // switchToMbl();
        switchToHbl();
        break;
      case 3:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ContainerStatusCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  
  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<ActionButtonItem|null>(null);

  const selectedElement = jobListContext.selectedElement as OceanQuoteListItem;

  const hasSelectedRow = !!selectedElement;

  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }

  return (
    <>
      {appContext.suntekSettings?.oceanAir?.enableFlexiTank && <div className='ifms-common-no-vertical-separator' style={{marginRight:'84px'}}>
        {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ? <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"} /> : <></>} */}
        <IconButton
          name='oeContainer-list-flexTank-button'
          onClick={() => setSelectedPopupMenuItem({ label: 'Flexi Tank', popupComponent: <FlexTank selectedElementId={selectedElement?.containerId} /> })}
          data-tooltip='Flexi Tank'
          icon={appContext.useDarkMode ? FlexiTank2_D : FlexiTank2} useText>
        </IconButton >
        {!!selectedPopupMenuItem && !!selectedPopupMenuItem.popupComponent ? (
          <IfmsModal isBlueHeader cancelOnEsc onCancel={onPopupClose} draggable isOpen={!!selectedPopupMenuItem} title={selectedPopupMenuItem?.popupLabel || selectedPopupMenuItem?.label} width={`${selectedPopupMenuItem?.popupWidth || 400}px`}>
            {cloneElement(selectedPopupMenuItem.popupComponent!, { onPopupClose })}
          </IfmsModal>
        ) : null}
      </div>}
    </>
  );
}

/**
 * Concrete implementation for ContainerStatus List screen
 *
 * @class ContainerStatusListView
 * @extends {AbstractListView}
 */
class ContainerStatusListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.Container,
      'OeContainerListItem',
      OeContainerListItem.calculateSearchParameters(search, OeContainerListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: OeContainerListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.Container, 'OeContainerListItem',
      OeContainerListItem.calculateSearchParameters(search, OeContainerListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  OeContainerListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      // {
      //   accessor: 'recognized',
      //   renderer: RecognizedFilterRenderer,
      //   searchValueExtractor: (v:any|null|undefined) => v,
      // },
      {
        accessor: 'moveType',
        renderer: MoveTypeFilterRenderer,
        searchValueExtractor: (v: any | null | undefined) => v,
      },
      {
        accessor: 'trStatus',
        renderer: RQFilterEventRenderer,
        searchValueExtractor: (v:any|null|undefined) => v !== null ? changeRQEvent(v): "",
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings, listCount: number, useDarkMode: boolean ): ListRowRenderer[] {
    return OeContainerListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      //if(x.dataIndex === 'containerNo') colRenderer.Cell = CntrNoRenderer;
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.align === XCol.XAlignType.Right) colRenderer.Cell = TextAlignRightRenderer;
      if(x.dataIndex === 'status') {
        colRenderer.Cell = IndicatorRenderer;
        // colRenderer.Header = () => <span></span>;
      }
      //else if(x.dataIndex === "refNo") colRenderer.Cell = RefRenderer;
      //else if(x.dataIndex === "mblNo") colRenderer.Cell = MblRenderer;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if (x.type === XCol.XCellType.Double) {
        if (x.dataIndex === 'pkgs'||x.dataIndex === 'kgs'||x.dataIndex === 'lbs')
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={0} />;
        else
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={2} />;
      }
      else if(x.dataIndex === 'trStatus'){
        colRenderer.Cell = (props: any) => <LastEventRenderer {...props} jobName={JobNames.OEContainer} />
        colRenderer.Header =  () => <div style={{gap:'4px', display:'flex'}}><img src={useDarkMode ? cntrGreen: cntrBlue} alt='' /><div>Container Tracking</div></div>;
      }
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    colRenderer.push({accessor: 'trStatus', changedValue: (value: any)=> {  return displayLastTrackingEvent(value)}});
    colRenderer.push({ accessor: 'pkgs', changedValue: (value: any) => { return value['pkgs'] } });
    colRenderer.push({ accessor: 'kgs', changedValue: (value: any) => { return value['kgs'] } });
    colRenderer.push({ accessor: 'lbs', changedValue: (value: any) => { return value['lbs'] } });
    colRenderer.push({ accessor: 'mblCreatedDate', changedValue: (value: any) => { return formatDate(value['mblCreatedDate']) } });
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'containerId',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  oceanExportContainerStatusList;
    const cols = OeContainerListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return <ContainerStatusCommands />
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: OeContainerListItem) {
    return ''
    // return row && row.id ? `/oceanExport/containerStatus/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Container List (OE)';
  };

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default ContainerStatusListView;
